import React, { type PropsWithChildren } from 'react';

import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { cn } from '@/lib/utils';

type SheetDrawerWrapperProps = PropsWithChildren<{
  toggle: () => void;
  title: string;
  description?: string;
  /** Kept for DrawerWrapper API compatibility. Backdrop always shows. */
  fullscreen?: boolean;
  className?: string;
}>;

/**
 * Right-hand Foundations Drawer with the same `{ toggle, title }` API as
 * IAP `DrawerWrapper`, so Reports can migrate independently of Offcanvas.
 */
const SheetDrawerWrapper: React.FC<SheetDrawerWrapperProps> = ({
  children,
  toggle,
  title,
  description,
  className,
}) => {
  return (
    <Drawer
      open
      swipeDirection="right"
      onOpenChange={(next) => {
        if (!next) toggle();
      }}
    >
      <DrawerContent
        className={cn('dark tw:[--drawer-inset:0.5rem]', className)}
      >
        <DrawerHeader>
          <DrawerTitle>{title}</DrawerTitle>
          {description ? (
            <DrawerDescription>{description}</DrawerDescription>
          ) : null}
        </DrawerHeader>
        {children}
      </DrawerContent>
    </Drawer>
  );
};

export default SheetDrawerWrapper;

export const SheetDrawerFooter: React.FC<PropsWithChildren> = ({
  children,
}) => {
  return (
    <DrawerFooter className="tw:w-full tw:pt-3!">
      {children}
    </DrawerFooter>
  );
};

export const SheetDrawerBody: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <div className="tw:flex tw:min-h-0 tw:min-w-0 tw:flex-1 tw:flex-col tw:overflow-hidden tw:rounded-xl tw:border tw:border-border tw:py-2.5 tw:pl-2.5 tw:pr-1">
      <ScrollArea
        className="tw:min-h-0 tw:h-full tw:min-w-0 tw:flex-1"
        viewportClassName="tw:pr-5"
      >
        {children}
      </ScrollArea>
    </div>
  );
};
